import { FaArrowDown, FaArrowUp } from 'react-icons/fa'
import { MultiSelectContainer } from './styles'
import { useEffect, useMemo, useRef, useState } from 'react'
import { LabelsReportQuery } from '../../domain/reportQuery'
import { institutionsForSelectedMenuFoodTypes } from '../../domain/institutionsForMenuFoodTypes'

type InstitutionsMultiSelectProps = {
  institutionOptions: IInstitutionData[]
  report: LabelsReportQuery
  reportQuery: LabelsReportQuery[]
  setReportQuery: React.Dispatch<React.SetStateAction<LabelsReportQuery[]>>
}

export const InstitutionsMultiSelect = ({
  institutionOptions,
  report,
  setReportQuery
}: InstitutionsMultiSelectProps): JSX.Element => {
  const [open, setOpen] = useState(false)
  const [institutionNames, setInstitutionNames] = useState<string[]>([])

  const filteredInstitutions = useMemo(
    () =>
      institutionsForSelectedMenuFoodTypes(
        institutionOptions,
        report.menuFoodTypesIds
      ),
    [institutionOptions, report.menuFoodTypesIds]
  )

  const addInstitutionId = (institutionId: number) => {
    setReportQuery(prev =>
      prev.map(row => {
        if (row.id !== report.id) {
          return row
        }
        if (row.institutionIds.includes(institutionId)) {
          return row
        }
        return {
          ...row,
          institutionIds: [...row.institutionIds, institutionId]
        }
      })
    )
  }

  const removeInstitutionId = (institutionIdToDelete: number) => {
    setReportQuery(prev =>
      prev.map(row =>
        row.id === report.id
          ? {
              ...row,
              institutionIds: row.institutionIds.filter(
                id => id !== institutionIdToDelete
              )
            }
          : row
      )
    )
  }

  const ref = useRef<HTMLDivElement>(null)

  const handleClickOutside = (e: MouseEvent) => {
    if (ref.current && !ref.current.contains(e.target as Node)) {
      setOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener('click', handleClickOutside)

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    const allowedIds = new Set(
      filteredInstitutions.map(institution => Number(institution.id))
    )
    setReportQuery(prev =>
      prev.map(row => {
        if (row.id !== report.id) {
          return row
        }
        const institutionIds = row.institutionIds.filter(id =>
          allowedIds.has(id)
        )
        if (institutionIds.length === row.institutionIds.length) {
          return row
        }
        return { ...row, institutionIds }
      })
    )
  }, [filteredInstitutions, report.id, setReportQuery])

  useEffect(() => {
    const names = report.institutionIds.map(id => {
      const institution = institutionOptions.find(option => option.id === id)
      return institution?.company_social_name ?? ''
    })
    setInstitutionNames(names.filter(Boolean))
  }, [institutionOptions, report.institutionIds])

  const displayValue =
    institutionNames.length > 0 ? institutionNames.join(', ') : 'Todas'

  return (
    <MultiSelectContainer className="row mb-6" ref={ref}>
      <label className="col-form-label fw-bold fs-6form-label fw-bold fs-6">
        Selecione as instituições
      </label>
      <div
        style={{ position: 'relative' }}
        onClick={() => setOpen(prev => !prev)}
      >
        <textarea
          rows={2}
          readOnly
          className="form-control form-control-solid pl-2"
          value={displayValue}
        />
        <div className="arraw-container">
          <div style={{ display: open ? 'none' : 'block' }}>
            <FaArrowDown />
          </div>
          <div style={{ display: !open ? 'none' : 'block' }}>
            <FaArrowUp />
          </div>
        </div>
      </div>
      <div className="items-container">
        <div
          style={{ display: open ? 'block' : 'none' }}
          className="items-container-inner"
        >
          {filteredInstitutions.length === 0 ? (
            <p className="p-2 mb-0 text-muted">
              Selecione um cardápio para listar as instituições.
            </p>
          ) : (
            filteredInstitutions.map(institution => (
              <div className="list-item" key={institution.id}>
                <input
                  type="checkbox"
                  name={`institution_ids${institution.id}${report.id}`}
                  id={`institution_ids${institution.id}${report.id}`}
                  value={institution.id}
                  checked={report.institutionIds.includes(
                    Number(institution.id)
                  )}
                  onChange={e =>
                    e.target.checked
                      ? addInstitutionId(Number(e.target.value))
                      : removeInstitutionId(Number(e.target.value))
                  }
                />
                <label
                  htmlFor={`institution_ids${institution.id}${report.id}`}
                  className="mx-2"
                >
                  {institution.company_social_name}
                </label>
              </div>
            ))
          )}
        </div>
      </div>
    </MultiSelectContainer>
  )
}
